/**
 * Action Links — the three ways an Action reaches the widget from the
 * page, detected in the MAIN bundle (they must work before any lazy
 * chunk exists), with the fire itself in the lazy actions.js chunk:
 *
 *   <a href="/contact" data-showpilot-action="pricing-quote">   (click)
 *   https://site.com/pricing#action=pricing-quote               (deep link)
 *   showpilot.open({ action: 'pricing-quote' })                 (page SDK)
 *
 * Only the Action id crosses from the page; the server accepts it only
 * if the operator declared it. A deep link is consumed ONCE (stripped
 * from the address bar) so a reload or a shared URL never re-fires it.
 */

export const ACTION_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
export const ACTION_ATTR = 'data-showpilot-action';

/** `#action=<id>` anywhere in the hash (`#action=x`, `#a=1&action=x`). */
export function readHashAction(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  for (const part of raw.split('&')) {
    if (!part.startsWith('action=')) continue;
    let id = part.slice(7);
    try {
      id = decodeURIComponent(id);
    } catch {
      /* keep raw */
    }
    return ACTION_ID.test(id) ? id : null;
  }
  return null;
}

/** The hash with `action=` removed — '' when nothing else was there. */
export function stripHashAction(hash: string): string {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const rest = raw.split('&').filter((p) => p && !p.startsWith('action='));
  return rest.length > 0 ? `#${rest.join('&')}` : '';
}

/**
 * Read and consume the deep link: the id comes back once and the
 * address bar loses it (replaceState, no navigation, no history entry),
 * so `pageUrl` on every later request is already clean.
 */
export function consumeHashAction(win: Window): string | null {
  let id: string | null = null;
  try {
    id = readHashAction(win.location.hash);
    if (!id) return null;
    const clean = stripHashAction(win.location.hash);
    win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search + clean);
  } catch {
    /* no history API, sandboxed frame — the id still fires once */
  }
  return id;
}

/**
 * A link on the page itself — `<a href="#action=request-a-quote">` — changes
 * the hash without a load. Consume it the same way, so the one URL an
 * operator copies works as a button href, in an email, or shared.
 */
export function installHashActionListener(win: Window, onAction: (id: string) => void): () => void {
  const handler = (): void => {
    const id = consumeHashAction(win);
    if (id) onAction(id);
  };
  win.addEventListener('hashchange', handler);
  return () => win.removeEventListener('hashchange', handler);
}

/** The Action id on the clicked element or its nearest ancestor, if valid. */
export function actionFromTarget(target: EventTarget | null): string | null {
  if (!target || typeof (target as Element).closest !== 'function') return null;
  const el = (target as Element).closest(`[${ACTION_ATTR}]`);
  const id = el?.getAttribute(ACTION_ATTR)?.trim() ?? '';
  return ACTION_ID.test(id) ? id : null;
}

/**
 * One delegated listener for the whole page. A click on an element
 * carrying the attribute goes to the agent and nowhere else — the
 * element's own href is its fallback for pages where the widget never
 * loaded (then nothing here runs and the link navigates as written).
 */
export function installActionClicks(doc: Document, onAction: (id: string) => void): () => void {
  const handler = (e: Event): void => {
    const m = e as MouseEvent;
    if (m.defaultPrevented || m.button) return;
    const id = actionFromTarget(e.target);
    if (!id) return;
    e.preventDefault();
    onAction(id);
  };
  doc.addEventListener('click', handler);
  return () => doc.removeEventListener('click', handler);
}
